import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	HostListener,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { OpenIdConnectAction } from "@obg/common.verification";
import { CustomerSupportService } from "@obg/core.customer-support";
import { batchActions } from "@obg/ngrx-utils";
import { CommonState, TermsAndConditionsConfigService } from "@obg/ngx.common";
import {
	AUTH_MESSAGE_CONTEXT,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerManagementAction,
	Jurisdiction,
	JurisdictionSelector,
	MessageService,
	MessageType,
	SESSION_LOGIN_EXPIRED_CODES,
	SessionSelector,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { PlatformService, WindowRef } from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { merge, Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { UpdatedTermsAndConditionsService } from "../updated-terms-and-conditions/updated-terms-and-conditions.service";

import { LatestTermsAndConditionsConfigService } from "./latest-terms-and-conditions.config";

@Component({
	selector: "obg-m-latest-terms-and-conditions-container",
	templateUrl: "./latest-terms-and-conditions.container.html",
	styleUrls: ["./latest-terms-and-conditions.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class LatestTermsAndConditionsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-latest-terms-and-conditions") class = true;

	@Output() close = new EventEmitter<void>();
	@Output() busy = new EventEmitter<boolean>();

	isHistoryEnabled = this.config.isHistoryEnabled();

	resourceKeys$ = this.store.pipe(select(this.sessionSelector.getTermsAndConditionsResourceKeys()), ignoreNil());

	showError = false;
	jurisdiction: Jurisdiction | undefined;

	private data$$: Subscription | undefined;
	private token: string | undefined;
	private supportUrl: string | undefined;

	@HostListener("window:resize")
	onResize(): void {
		this.setInnerHeightVariable();
	}

	constructor(
		private store: Store<CommonState | CoreContentState>,
		private sessionSelector: SessionSelector,
		private customerManagementAction: CustomerManagementAction,
		private updatedTermsAndConditionsService: UpdatedTermsAndConditionsService,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef,
		private config: TermsAndConditionsConfigService,
		private platformService: PlatformService,
		private window: WindowRef,
		private openIdConnectAction: OpenIdConnectAction,
		private latestTermsAndConditionsConfigService: LatestTermsAndConditionsConfigService,
		private customerSupportService: CustomerSupportService
	) {}

	ngOnInit(): void {
		this.setInnerHeightVariable();

		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => (this.jurisdiction = jurisdiction))
		);

		const token$ = this.store
			.pipe(select(this.sessionSelector.getTermsAndConditionsToken()))
			.pipe(tap((token) => (this.token = token)));

		const acceptError$ = this.messageService.consume$({
			context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
			messageType: MessageType.Error,
		});

		const loginError$ = this.messageService.consume$({
			context: [
				AUTH_MESSAGE_CONTEXT.login,
				AUTH_MESSAGE_CONTEXT.loginWithToken,
				AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
			],
			messageType: MessageType.Error,
			excludeCodes: SESSION_LOGIN_EXPIRED_CODES,
		});

		const acceptSuccess$ = this.messageService
			.consume$({
				context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
				messageType: MessageType.Success,
			})
			.pipe(tap(() => this.close.emit()));

		const errors$ = merge(acceptError$, loginError$).pipe(
			tap(() => {
				this.busy.emit(false);
				this.showError = true;
			})
		);

		const getSupportUrl$ = this.customerSupportService.customerSupportUrl$.pipe(
			tap((supportUrl: string) => {
				this.supportUrl = supportUrl;
			})
		);

		this.data$$ = merge(jurisdiction$, token$, errors$, acceptSuccess$, getSupportUrl$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}

	setInnerHeightVariable(): void {
		if (this.platformService.isBrowser) {
			const windowHeight = this.window.native.innerHeight;
			this.window.native.document.documentElement.style.setProperty("--window-inner-height", `${windowHeight}px`);
		}
	}

	accept(): void {
		if (!this.isHistoryEnabled) {
			this.busy.emit(true);
			this.updatedTermsAndConditionsService.acceptTermsAndConditions();

			return;
		}

		if (!this.token) {
			return;
		}
		this.busy.emit(true);
		this.store.dispatch(this.customerManagementAction.acceptLatestTermsAndConditions(this.token));
	}

	closeModal(): void {
		if (this.jurisdiction === Jurisdiction.COGA) {
			this.handleCogaJurisdiction();

			return;
		}

		if (this.latestTermsAndConditionsConfigService.latestTermsAndConditionsEnabled() && this.supportUrl) {
			window.location.href = this.supportUrl;
		}
		this.store.dispatch(
			batchActions(
				this.customerManagementAction.dismissAcceptTermsAndConditions(),
				this.openIdConnectAction.clearOpenIdConnectToken()
			)
		);
		this.close.emit();
	}

	private handleCogaJurisdiction(): void {
		this.store.dispatch(
			batchActions(
				this.customerManagementAction.dismissAcceptTermsAndConditions(),
				this.customerManagementAction.deleteCredentials()
			)
		);
		this.close.emit();
	}
}
